import { Drawer } from '@mantine/core';
import { Box, Flex } from '@huspy/briks-web';
import { Divider } from '@huspy/styled-system/jsx';
import { Cancel } from '@huspy/briks-icons';

type BaseSlidoProps = {
  opened: boolean;
  close: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'md' | 'lg' | 'xl' | '100%';
  hasDivider?: boolean;
  showCloseButton?: boolean;
  inTopOfAll?: boolean;
  dark?: boolean;
};

// Helper function to get theme styles
const getThemeStyles = (isDark: boolean) => {
  const darkStyles = {
    contentBg: '#141413',
    titleColor: '#FFFFFF',
    iconColor: '#A7A8A9',
    dividerStyle: { borderColor: '#404040' },
    footerBg: '#1a1a1a',
    footerBorderColor: '#404040',
    darkModeStyles: { outline: 'none', '--mantine-focus-ring': 'none' } as any,
    overlayStyles: { outline: 'none', border: 'none' },
    contentStyles: {
      outline: 'none', border: 'none', boxShadow: 'none', '--mantine-focus-ring': 'none',
    } as any,
    headerStyles: { backgroundColor: '#141413', outline: 'none' },
    bodyStyles: { outline: 'none' },
    flexStyles: { outline: 'none' },
  };

  const lightStyles = {
    contentBg: 'white',
    titleColor: undefined,
    iconColor: undefined,
    dividerStyle: undefined,
    footerBg: 'base.white',
    footerBorderColor: 'neutral.100',
    darkModeStyles: undefined,
    overlayStyles: undefined,
    contentStyles: undefined,
    headerStyles: undefined,
    bodyStyles: undefined,
    flexStyles: undefined,
  };

  return isDark ? darkStyles : lightStyles;
};

export const BaseSlido = ({
  opened,
  close,
  title,
  children,
  footer,
  size = 'lg',
  hasDivider = true,
  showCloseButton = true,
  inTopOfAll = false,
  dark = false,
}: BaseSlidoProps): React.JSX.Element => {
  const drawerProps: any = {
    opened,
    onClose: close,
    position: 'right',
    size,
    ...(inTopOfAll && { zIndex: 300 }),
  };

  const styles = getThemeStyles(dark);

  return (
    <Drawer.Root { ...drawerProps } style={ styles.darkModeStyles }>
      <Drawer.Overlay style={ styles.overlayStyles } />
      <Drawer.Content bg={ styles.contentBg } style={ styles.contentStyles }>
        <Drawer.Header p='0' style={ styles.headerStyles }>
          <Drawer.Title style={ { width: '100%', ...(styles.titleColor && { color: styles.titleColor }) } }>
            {title}
          </Drawer.Title>
          {showCloseButton && (
            <Flex
              justify='center'
              align='center'
              w='11'
              h='11'
              mr='8'
              cursor='pointer'
              { ...(!dark && { boxShadow: 'shadow-4' }) }
              borderRadius='full'
              onClick={ close }
              flexShrink={ 0 }
            >
              <Cancel
                height={ 20 }
                width={ 20 }
                data-test='cancel-slido'
                style={ styles.iconColor ? { color: styles.iconColor } : undefined }
              />
            </Flex>
          )}
        </Drawer.Header>
        {hasDivider && <Divider style={ styles.dividerStyle } />}
        <Drawer.Body h='calc(100% - 101px)' p='0' style={ styles.bodyStyles }>
          <Flex
            direction='column'
            pos='relative'
            justify='space-between'
            h='100%'
            data-testid='slido-body'
            style={ styles.flexStyles }
          >
            <Box>{children}</Box>
            {footer && (
              <Box
                position='sticky'
                bottom='0'
                w='100%'
                px='6'
                py='4'
                { ...(dark ? {
                  style: {
                    backgroundColor: styles.footerBg,
                    borderTop: `1px solid ${styles.footerBorderColor}`,
                  },
                } : {
                  bg: styles.footerBg,
                  borderTop: '1px solid',
                  borderColor: styles.footerBorderColor,
                }) }
              >
                {footer}
              </Box>
            )}
          </Flex>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
};
